import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TabBroadcast from '../components/communications/TabBroadcast';
import TabAnnouncements from '../components/communications/TabAnnouncements';
import TabTemplates from '../components/communications/TabTemplates';
import TabAccounts from '../components/communications/TabAccounts';
import TabInbox from '../components/communications/TabInbox';
import { EMZI_COLORS } from '../styles/emziCI';

/**
 * CommunicationsHub - Unified interface for Broadcast, Announcements, and Templates
 * RBAC: Management/Admin full access, Supervisor read-only, Executor no access
 */
const CommunicationsHub = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, isManagement, isSupervisor, isExecutor } = useAuth();
  
  const [activeTab, setActiveTab] = useState('inbox'); // Default to inbox
  const [prefilledData, setPrefilledData] = useState(null);

  // ALL ROLES have access to Communications Hub (different tabs have different permissions)
  // Inbox: All roles (Executor, Supervisor, Management, Admin)
  // Broadcast/Announcements/Templates: Management/Admin/Supervisor
  // Accounts: Management/Admin/Supervisor

  // Route Guard: Executor can ONLY access Inbox tab
  useEffect(() => {
    const userRole = user?.role;
    if (userRole === 'executor') {
      // Force redirect to inbox if trying to access other tabs
      const currentTab = searchParams.get('tab');
      if (currentTab && currentTab !== 'inbox') {
        navigate('/communications?tab=inbox', { replace: true });
      }
      // Ensure active tab is always inbox for executors
      setActiveTab('inbox');
    }
  }, [user, searchParams, navigate]);

  // Handle tab from URL query params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['inbox', 'broadcast', 'announcements', 'templates', 'accounts'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Handle prefilled data from navigation state (post-pack nudge)
  useEffect(() => {
    if (location.state?.prefilled) {
      setPrefilledData(location.state.prefilled);
      setActiveTab('announcements');
      setSearchParams({ tab: 'announcements' });
    }
  }, [location.state]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Define all tabs
  const allTabs = [
    { id: 'inbox', label: 'Inbox', icon: '📬', description: 'Internal messages', roles: ['executor', 'supervisor', 'management_functional', 'management_global', 'admin'] },
    { id: 'broadcast', label: 'Broadcast', icon: '📡', description: 'WhatsApp Broadcast Mode', roles: ['supervisor', 'management_functional', 'management_global', 'admin'] },
    { id: 'announcements', label: 'Announcements', icon: '📢', description: 'System-wide announcements', roles: ['supervisor', 'management_functional', 'management_global', 'admin'] },
    { id: 'templates', label: 'Templates', icon: '📝', description: 'Message templates', roles: ['management_functional', 'management_global', 'admin'] },
    { id: 'accounts', label: 'Accounts', icon: '💬', description: 'WhatsApp accounts', roles: ['supervisor', 'management_functional', 'management_global', 'admin'] }
  ];

  // Filter tabs based on user role
  const tabs = allTabs.filter(tab => {
    const userRole = user?.role;
    return tab.roles.includes(userRole);
  });

  return (
    <div 
      className="nexus-content bg-white"
      style={{
        height: '100vh',
        maxHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div 
        className="px-6 py-5 text-white flex-shrink-0 shadow-lg"
        style={{ background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)' }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-wide">
              {(isExecutor() || isSupervisor()) && !isManagement() && !isAdmin() ? 'Inbox' : 'Communications Hub'}
            </h1>
            <p className="text-sm text-white opacity-90 mt-1">
              {(isExecutor() || isSupervisor()) && !isManagement() && !isAdmin()
                ? 'Your messages, approvals, and tasks'
                : 'Unified interface for broadcasts, announcements, and templates'
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-white opacity-75">
              Role: {user?.role?.replace(/_/g, ' ').toUpperCase()}
            </p>
            {isSupervisor() && !isManagement() && !isAdmin() && (
              <p className="text-xs text-yellow-200 mt-1">
                📖 Read-Only Access
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation - Sticky */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20 shadow-sm">
        <div className="px-6">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 border-b-2 transition-all whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50 font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
                style={{ minHeight: '56px' }}
              >
                <span className="text-lg sm:text-xl">{tab.icon}</span>
                <div className="text-left">
                  <div className="text-sm sm:text-base font-semibold">{tab.label}</div>
                  <div className="text-xs opacity-75 hidden sm:block">{tab.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div 
        className="flex-1"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {activeTab === 'inbox' && <TabInbox />}
        {activeTab === 'broadcast' && <TabBroadcast />}
        {activeTab === 'announcements' && (
          <TabAnnouncements prefilledData={prefilledData} />
        )}
        {activeTab === 'templates' && <TabTemplates />}
        {activeTab === 'accounts' && <TabAccounts />}
      </div>

    </div>
  );
};

export default CommunicationsHub;
